/**
 * Web Push (browser) helpers.
 *
 * Handles requesting permission, subscribing the device, and keeping the
 * backend's subscription registry in sync so reminders/announcements can be
 * delivered as real push notifications even when the app is closed.
 */

import { apiHelper } from "./api";

const PUSH_ENABLED_KEY = "mivent_push_enabled";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// Convert a base64url VAPID public key to a Uint8Array (required by Safari).
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    if (!navigator.serviceWorker.controller) {
      await navigator.serviceWorker.register("/service-worker.js");
    }
    return await navigator.serviceWorker.ready;
  } catch (err) {
    console.warn("Service worker not ready for push:", err);
    return null;
  }
}

async function getVapidPublicKey() {
  try {
    const res = await apiHelper.get("/push/vapid-public-key");
    const key = res?.data || res;
    return typeof key === "string" ? key : null;
  } catch (err) {
    console.warn("Could not fetch VAPID public key:", err);
    return null;
  }
}

export async function getCurrentPushSubscription() {
  const reg = await getServiceWorkerRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Subscribe this device for push notifications.
 * Triggers the browser permission prompt if not yet granted.
 *
 * @returns {{ supported: boolean, granted: boolean, subscribed: boolean }}
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return { supported: false, granted: false, subscribed: false };

  if (Notification.permission === "denied") {
    return { supported: true, granted: false, subscribed: false, denied: true };
  }

  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { supported: true, granted: false, subscribed: false };
    }
  }

  const vapidPublicKey = await getVapidPublicKey();
  if (!vapidPublicKey) {
    return { supported: true, granted: true, subscribed: false, error: "no-vapid-key" };
  }

  const reg = await getServiceWorkerRegistration();
  if (!reg) {
    return { supported: true, granted: true, subscribed: false, error: "no-sw" };
  }

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  const json = subscription.toJSON();
  const payload = {
    endpoint: subscription.endpoint,
    keys: json.keys,
    expirationTime: subscription.expirationTime || null,
    deviceLabel:
      typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : null,
  };

  try {
    await apiHelper.post("/push/subscribe", payload);
  } catch (err) {
    // 409 = duplicate key race (two tabs mounted at once POSTed the same
    // endpoint). The subscription is already saved by the winning request,
    // so treat it as success instead of surfacing an error.
    if (err?.response?.status !== 409) throw err;
  }
  localStorage.setItem(PUSH_ENABLED_KEY, "true");

  return { supported: true, granted: true, subscribed: true };
}

/**
 * Unsubscribe this device and remove the subscription from the backend.
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;

  try {
    const reg = await getServiceWorkerRegistration();
    if (reg) {
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe().catch(() => {});
        await apiHelper
          .delete("/push/subscribe", { data: { endpoint } })
          .catch(() => {});
      }
    }
  } catch (err) {
    console.warn("Error unsubscribing from push:", err);
  }

  localStorage.removeItem(PUSH_ENABLED_KEY);
}

/**
 * Best-effort: re-establish the push subscription on login without prompting.
 * Only runs when permission was already granted (user opted in previously).
 *
 * Guarded against concurrent/duplicate calls (React StrictMode double-effects
 * and auth state churn used to fire duplicate POST /push/subscribe).
 */
let syncPushInflight = null;
let lastSyncPushAt = 0;
const SYNC_PUSH_COOLDOWN_MS = 60 * 1000;

export async function syncPushOnLogin() {
  if (!isPushSupported()) return;
  if (Notification.permission !== "granted") return;
  if (syncPushInflight) return syncPushInflight;
  if (Date.now() - lastSyncPushAt < SYNC_PUSH_COOLDOWN_MS) return;

  syncPushInflight = (async () => {
  try {
    const reg = await getServiceWorkerRegistration();
    if (!reg) return;

    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      const json = existing.toJSON();
      await apiHelper.post("/push/subscribe", {
        endpoint: existing.endpoint,
        keys: json.keys,
        expirationTime: existing.expirationTime || null,
        deviceLabel:
          typeof navigator !== "undefined"
            ? navigator.userAgent.slice(0, 200)
            : null,
      }).catch(() => {});
      return;
    }

    await subscribeToPush().catch(() => {});
  } catch (err) {
    console.warn("Push sync on login skipped:", err);
  } finally {
    lastSyncPushAt = Date.now();
    syncPushInflight = null;
  }
  })();
  return syncPushInflight;
}

export function isPushEnabledOnDevice() {
  if (!isPushSupported()) return false;
  return Notification.permission === "granted";
}

export default {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  syncPushOnLogin,
  getCurrentPushSubscription,
  isPushEnabledOnDevice,
};
